import React from "react";
import { Target, Eye, CheckCircle2 } from "lucide-react";
import { IMAGES, getValues } from "@/lib/siteContent";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";

const PageHero = ({ kicker, title, lead }) => (
  <section className="relative pt-24 pb-16 lg:pt-32 lg:pb-20 bg-white">
    <div className="absolute inset-0 hex-pattern opacity-100 pointer-events-none" aria-hidden="true" />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
      <div className="text-xs uppercase tracking-[0.22em] text-teal-700 mb-4">{kicker}</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-slate-900 tracking-tight max-w-4xl">{title}</h1>
      {lead && <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">{lead}</p>}
    </div>
  </section>
);

const About = () => {
  const { language, t } = useLanguage();
  const VALUES = getValues(language);

  return (
    <div data-testid="about-page">
      <PageHero
        kicker={t("about.kicker")}
        title={<>{t("about.titlePrefix")} <em className="not-italic text-teal-700">{t("about.titleEm")}</em> {t("about.titleSuffix")}</>}
        lead={t("about.lead")}
      />

      {/* Story + image */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <img
              src={IMAGES.aboutOffice}
              alt={t("about.imageAlt")}
              className="w-full rounded-2xl object-cover aspect-[5/4] shadow-xl shadow-slate-900/5"
              data-testid="about-image"
            />
          </div>
          <div className="lg:col-span-6">
            <h2 className="font-display text-3xl lg:text-4xl text-slate-900 tracking-tight">
              {t("about.storyTitle")}
            </h2>
            <div className="mt-5 space-y-4 text-slate-600 leading-relaxed">
              <p>{t("about.storyP1")}</p>
              <p>{t("about.storyP2")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 lg:py-24 bg-[#F0FDFA]" data-testid="mission-vision">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10">
          <div className="p-8 lg:p-10 rounded-2xl bg-white border border-slate-200">
            <div className="inline-flex items-center justify-center h-12 w-12 rounded-lg bg-teal-50 text-teal-700">
              <Target size={22} />
            </div>
            <h3 className="mt-5 font-display text-2xl text-slate-900">{t("about.missionTitle")}</h3>
            <p className="mt-3 text-slate-600 leading-relaxed">
              {t("about.missionText")}
            </p>
          </div>
          <div className="p-8 lg:p-10 rounded-2xl bg-white border border-slate-200">
            <div className="inline-flex items-center justify-center h-12 w-12 rounded-lg bg-teal-50 text-teal-700">
              <Eye size={22} />
            </div>
            <h3 className="mt-5 font-display text-2xl text-slate-900">{t("about.visionTitle")}</h3>
            <p className="mt-3 text-slate-600 leading-relaxed">
              {t("about.visionText")}
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 lg:py-28 bg-white" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-6">
              <div className="text-xs uppercase tracking-[0.22em] text-teal-700 mb-3">{t("about.valuesKicker")}</div>
              <h2 className="font-display text-3xl lg:text-5xl text-slate-900 tracking-tight">
                {t("about.valuesTitle")}
              </h2>
            </div>
            <div className="lg:col-span-6 text-slate-600">
              {t("about.valuesLead")}
            </div>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {VALUES.map((v, idx) => (
              <div
                key={v}
                className="flex items-start gap-3 p-6 rounded-xl border border-slate-200 card-hover"
                data-testid={`value-card-${idx}`}
              >
                <CheckCircle2 size={20} className="text-teal-700 mt-0.5" />
                <div>
                  <div className="font-medium text-slate-900">{v}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="py-20 border-t border-slate-100 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <h3 className="font-display text-3xl lg:text-4xl text-slate-900 tracking-tight">
            {t("about.ctaTitle")}
          </h3>
          <Link
            to="/iletisim"
            className="mt-8 inline-flex items-center gap-2 px-6 h-12 rounded-full bg-teal-700 text-white text-sm font-medium hover:bg-teal-800 transition-colors"
            data-testid="about-cta-contact"
          >
            {t("about.ctaButton")}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
